/**
 * Simple Fetch API wrapper.
 */

class FetchApi {
	/**
	 * Constructor.
	 *
	 * @param {Object} internalApi
	 */
	constructor( internalApi ) {
		this.internalApi = internalApi;
		this.headers = {
			Accept: 'application/json',
		};

		// Add the nonce auth so we can accurately determine logged in state.
		if ( QalaBuy.wpnonce ) {
			this.headers[ 'X-WP-Nonce' ] = QalaBuy.wpnonce;
		}
	}

	/**
	 * Make a GET request.
	 * Handles errors and content types for us.
	 *
	 * @param {string} endpoint
	 * @param {Object} requestHeaders
	 */
	async get( endpoint, requestHeaders ) {
		const defaultHeaders = this.headers;
		requestHeaders = { ...defaultHeaders, ...( requestHeaders || {} ) };

		return await window.fetch( endpoint, {
			method: 'GET',
			headers: requestHeaders,
		} )
			.then( this._handleError )
			.catch( this._catchError );
	}

	/**
	 * Make a POST request.
	 * Handles errors and content types for us.
	 *
	 * @param {string} endpoint
	 * @param {Object} requestHeaders
	 * @param {Object} requestBody
	 */
	async post( endpoint, requestHeaders, requestBody ) {
		const defaultHeaders = this.headers;
		requestHeaders = { ...defaultHeaders, ...( requestHeaders || {} ) };

		return await window.fetch( endpoint, {
			method: 'POST',
			headers: requestHeaders,
			body: JSON.stringify( requestBody ),
		} )
			.then( this._handleError )
			.then( this._handleContentType )
			.catch( this._catchError );
	}

	/**
	 * Handles errors from a request.
	 *
	 * @param {Object} response
	 */
	async _handleError( response ) {
		if ( ! response.ok ) {
			// If our request failed we want to get the json response.
			const errorResponse = await new Promise( resolve => response.json()
				.then( json => resolve( json ) )
			);
			// create error object and reject if not a 2xx response code
			const err = new Error( errorResponse.message, { cause: errorResponse.code } );
			err.response = response;
			err.status = response.status;
			throw err;
		}
		if ( response.status === 204 ) {
			// If the response is empty we have to handle that as well.
			const err = new Error( 'Could not fetch this product.', { cause: 'no_product' } );
			err.response = response;
			err.status = response.status;
			throw err;
		}

		return response;
	}

	/**
	 * Handles content types from a request.
	 * Note: currently not in use because we needed the full response back to store nonce.
	 *
	 * @param {Object} res
	 */
	_handleContentType = res => {
		const contentType = res.headers.get( 'content-type' );
		if ( contentType && contentType.includes( 'application/json' ) ) {
			this.internalApi.saveNonceHeaders( res.headers );
			return res;
		}
		return Promise.reject( 'Oops, we haven\'t got JSON!' );
	};

	/**
	 * Handles throwing errors from a request.
	 *
	 * @param {Object} err
	 */
	_catchError( err ) {
		return err;
	}
}

export default FetchApi;
